"use client"

import { usePage } from "@inertiajs/react"
import { useState } from "react"
import {
  Button as AriaButton,
  Dialog as AriaDialog,
  DialogTrigger as AriaDialogTrigger,
  Popover as AriaPopover,
} from "react-aria-components"

import AppLogoIcon from "@/components/app-logo-icon"
import { MarketingUserMenu } from "@/components/marketing/user-menu"
import { Button } from "@/components/ui/button"
import { Link } from "@/components/ui/link"
import { publicNavItems } from "@/constants/public-nav"
import { dashboardPath, signInPath, signUpPath } from "@/routes"
import type { SharedData } from "@/types"

import { cx } from "@uui/utils/cx"

type PublicHeaderVariant = "brand" | "light"

interface PublicHeaderProps {
  className?: string
  // "brand" = solid navy masthead (default). "light" = translucent ink-on-canvas
  // bar that sits on the Oat ground — recessive, content-first chrome.
  variant?: PublicHeaderVariant
}

export const PublicHeader = ({
  className,
  variant = "brand",
}: PublicHeaderProps) => {
  const { visitor } = usePage<SharedData>().props
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const isLight = variant === "light"
  // The wordmark is the way back to the dashboard once you're in. Signed-out
  // visitors land on the root (Featured / Playlists#index).
  const homeHref = visitor.authenticated ? dashboardPath() : "/"

  // The light variant mirrors the /design nav's type discipline: text-sm,
  // medium-weight links shifting tertiary -> primary, with /design's focus
  // treatment. The brand variant keeps its heavier on-navy chrome.
  const navLinkClass = isLight
    ? "outline-focus-ring rounded-xs text-sm font-medium text-tertiary whitespace-nowrap transition-colors hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2"
    : "text-md rounded-lg px-3 py-2 font-semibold text-secondary_on-brand transition duration-100 ease-linear hover:text-secondary_on-brand"
  const triggerIconClass = isLight ? "text-primary" : "text-secondary_on-brand"

  return (
    <header
      className={cx(
        "relative w-full",
        isLight
          ? "border-secondary bg-surface-base/90 sticky top-0 z-30 border-b backdrop-blur-sm"
          : "bg-brand-section [&_svg_path.fill-fg-primary]:fill-fg-white flex h-18 items-center justify-center md:h-20",
        className,
      )}
    >
      <div
        className={cx(
          "max-w-container mx-auto flex items-center px-4 lg:px-8",
          // h-11 (44px) = /design nav's exact footprint: text-sm line (20px) +
          // py-3 (24px). items-center lands the labels 12px off the hairline,
          // matching /design; the taller button/mark centre within.
          isLight ? "gap-6 py-3" : "size-full flex-1 md:px-8",
        )}
      >
        <div className="flex w-full items-center justify-between gap-4">
          <div className="flex flex-1 items-center gap-5">
            <Link
              href={homeHref}
              variant="muted"
              aria-label="Lists"
              className="outline-focus-ring inline-flex items-center gap-2 rounded-xs whitespace-nowrap transition hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2"
            >
              {/* A tile-less variant of the authed rail's lockup: the elephant
                  glyph beside the "Lists" wordmark, toned for the bar it sits on
                  and held to the nav's quiet type scale (the light bar keeps its
                  ~44px footprint). The yellow tile stays an authed-shell flourish. */}
              <AppLogoIcon
                aria-hidden="true"
                className={cx(
                  "w-auto",
                  isLight ? "text-primary h-5" : "text-primary_on-brand h-8",
                )}
              />
              <span
                className={cx(
                  "font-bold tracking-tight",
                  isLight
                    ? "text-primary text-sm"
                    : "text-primary_on-brand text-lg",
                )}
              >
                Lists
              </span>
            </Link>

            {/* Desktop navigation */}
            <nav className="hidden md:block">
              <ul
                className={cx("flex items-center", isLight ? "gap-5" : "gap-1")}
              >
                {publicNavItems.map((navItem) => (
                  <li key={navItem.label}>
                    <Link
                      href={navItem.href}
                      className={navLinkClass}
                      variant="muted"
                    >
                      {navItem.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          <div className="hidden items-center gap-3 md:flex">
            {visitor.authenticated ? (
              <MarketingUserMenu />
            ) : isLight ? (
              <>
                {/* Log in: quiet ink link, same type treatment as the nav. */}
                <Link
                  href={signInPath()}
                  variant="muted"
                  className={navLinkClass}
                >
                  Log in
                </Link>
                {/* Sign up: navy "ink-stamp" — composed from the UUI secondary
                    button + Batyr navy tokens (no new kit styles). Navy is the
                    system's "weight"; this keeps the one yellow moment for the
                    in-page action. */}
                <Button
                  href={signUpPath()}
                  color="secondary"
                  size="sm"
                  className="bg-surface-strong text-primary_on-brand hover:bg-surface-strong_subtle hover:text-primary_on-brand ring-transparent"
                >
                  Sign up
                </Button>
              </>
            ) : (
              <>
                <Button href={signInPath()} color="secondary" size="lg">
                  Log in
                </Button>
                <Button href={signUpPath()} color="primary" size="lg">
                  Sign up
                </Button>
              </>
            )}
          </div>

          {/* Mobile menu trigger */}
          <AriaDialogTrigger>
            <AriaButton
              onPress={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle navigation menu"
              className="group hover:bg-primary_hover ml-auto cursor-pointer rounded-lg p-2 outline-none focus-visible:ring-2 focus-visible:ring-offset-2 md:hidden"
            >
              <svg
                aria-hidden="true"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  className={cx(
                    triggerIconClass,
                    isMobileMenuOpen ? "hidden" : "block",
                  )}
                  d="M3 12H21M3 6H21M3 18H21"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  className={cx(
                    triggerIconClass,
                    isMobileMenuOpen ? "block" : "hidden",
                  )}
                  d="M18 6L6 18M6 6L18 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </AriaButton>
            <AriaPopover
              className="w-full md:hidden"
              placement="bottom end"
              offset={0}
              containerPadding={0}
            >
              <AriaDialog className="bg-primary w-screen shadow-lg outline-none">
                <nav className="border-secondary flex flex-col gap-4 border-t p-4">
                  <ul className="flex flex-col gap-2">
                    {publicNavItems.map((item) => (
                      <li key={item.label}>
                        <Link
                          href={item.href}
                          className="text-md text-primary hover:bg-primary_hover block w-full rounded-md px-2 py-2 font-semibold"
                        >
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4 flex flex-col gap-3">
                    {visitor.authenticated ? (
                      <MarketingUserMenu />
                    ) : (
                      <>
                        <Button href={signUpPath()} size="lg">
                          Sign up
                        </Button>
                        <Button href={signInPath()} color="secondary" size="lg">
                          Log in
                        </Button>
                      </>
                    )}
                  </div>
                </nav>
              </AriaDialog>
            </AriaPopover>
          </AriaDialogTrigger>
        </div>
      </div>
    </header>
  )
}
